// SPDX-License-Identifier: AGPL-3.0-or-later

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {http} from '@app/features/platform/transport/RestTransport';
import {Logger} from '@app/features/platform/utils/AppLogger';
import MediaEngine from '@app/features/voice/engine/MediaEngineFacade';

const logger = new Logger('RoomSceneCommands');

// A state that lasts less than its delay sends nothing, so quick toggles end in at most one request. Closing waits
// longer: a short look at a text channel during the call sends nothing at all.
// Each sent change reaches the whole LiveKit room: the TEMPORARY trigger is at the API's VOICE_ROOM_SCENE limit.
export const SCENE_REPORT_DELAY_MS = 2000;
export const SCENE_CLOSE_DELAY_MS = 15_000;
// A refused request (429, 503) is tried once more after this; a later failure is left to the next change.
export const SCENE_RETRY_DELAY_MS = 30_000;

interface SceneReport {
	room: object;
	channelId: string;
	connectionId: string;
	open: boolean;
	sent: boolean;
	timer: ReturnType<typeof setTimeout> | null;
	busy: boolean;
	retried: boolean;
}

// One voice connection at a time, so one report.
let report: SceneReport | null = null;

// Tells the server whether my connection shows the 3D room (it writes `micup.scene`, which the activity report counts).
// Only the state that held for SCENE_REPORT_DELAY_MS goes, only when the server holds another, one request at a time.
// A connection that is no longer the call's gets nothing: its participant left and the mark with it. A new room
// object (a fresh LiveKit participant) starts with no mark.
export function reportSceneOpen(room: object, channelId: string, connectionId: string, open: boolean): void {
	if (report?.room !== room || report.channelId !== channelId || report.connectionId !== connectionId) {
		if (report?.timer) clearTimeout(report.timer);
		report = {room, channelId, connectionId, open, sent: false, timer: null, busy: false, retried: false};
	}
	if (report.open !== open) report.retried = false;
	report.open = open;
	schedule(report, open ? SCENE_REPORT_DELAY_MS : SCENE_CLOSE_DELAY_MS);
}

// After a LiveKit (re)connect the participant may have lost its attributes: an open view is sent again.
export function resendSceneOpen(room: object, channelId: string, connectionId: string): void {
	if (report?.room !== room || report.channelId !== channelId || report.connectionId !== connectionId) return;
	if (!report.open) return;
	report.sent = false;
	report.retried = false;
	schedule(report, SCENE_REPORT_DELAY_MS);
}

function schedule(target: SceneReport, delay: number): void {
	if (target.timer) clearTimeout(target.timer);
	target.timer = setTimeout(() => {
		target.timer = null;
		void flush(target);
	}, delay);
}

async function flush(target: SceneReport): Promise<void> {
	if (report !== target || target.busy || target.open === target.sent) return;
	if (MediaEngine.channelId !== target.channelId || MediaEngine.connectionId !== target.connectionId) {
		report = null;
		return;
	}
	const open = target.open;
	target.busy = true;
	let ok = true;
	try {
		await http.put(Endpoints.CHANNEL_VOICE_SEAT_SCENE(target.channelId), {
			body: {connection_id: target.connectionId, open},
		});
		target.sent = open;
	} catch (error) {
		ok = false;
		logger.warn('3D room view report failed', error);
	} finally {
		target.busy = false;
	}
	if (target.timer) return;
	// A change that came while the request was out goes now; a failure once more, later (a change during a failed
	// request can only return to what the server holds).
	if (ok) await flush(target);
	else if (!target.retried) {
		target.retried = true;
		schedule(target, SCENE_RETRY_DELAY_MS);
	}
}
